import * as React from 'react';

export type Theme = 'light' | 'dark' | 'system';

interface ThemeContextValue {
  /** A választott MÓD (`system` = az operációs rendszer beállítását követi). */
  theme: Theme;
  /** A ténylegesen alkalmazott téma (a `system` itt már feloldva). */
  resolvedTheme: 'light' | 'dark';
  setTheme: (theme: Theme) => void;
}

const ThemeContext = React.createContext<ThemeContextValue | undefined>(undefined);
// A választás a böngészőben él (`localStorage`, kulcs: 'theme') — eszközönként; az `index.html` inline szkriptje
// ugyanebből olvas a paint előtt. Az ALAP a VILÁGOS (user-döntés, Dóri/Ilcsa #50): aki korábban kézzel váltott,
// annak a tárolt választása marad.
const STORAGE_KEY = 'theme';
const DARK_QUERY = '(prefers-color-scheme: dark)';

function isTheme(v: string | null): v is Theme {
  return v === 'light' || v === 'dark' || v === 'system';
}

function systemTheme(): 'light' | 'dark' {
  return window.matchMedia(DARK_QUERY).matches ? 'dark' : 'light';
}

// A <html>-re a theme-{dark|light} (glass-réteg) ÉS a dark (shadcn) osztály is kell.
function apply(resolved: 'light' | 'dark'): void {
  const html = document.documentElement;
  html.classList.remove('theme-dark', 'theme-light', 'dark');
  html.classList.add(`theme-${resolved}`);
  if (resolved === 'dark') html.classList.add('dark');
}

function readStored(): Theme | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return isTheme(v) ? v : null;
  } catch {
    // tiltott tároló (privát mód, letiltott sütik) → az alapérték érvényes
    return null;
  }
}

export function ThemeProvider({
  children,
  defaultTheme = 'light',
}: {
  children: React.ReactNode;
  defaultTheme?: Theme;
}): JSX.Element {
  const [theme, setThemeState] = React.useState<Theme>(() => readStored() ?? defaultTheme);
  const [system, setSystem] = React.useState<'light' | 'dark'>(() => systemTheme());

  // a `system` mód ÉLŐBEN követi az operációs rendszert (pl. napszak szerinti automatikus váltás)
  React.useEffect(() => {
    const mql = window.matchMedia(DARK_QUERY);
    const onChange = (): void => setSystem(mql.matches ? 'dark' : 'light');
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  const resolvedTheme = theme === 'system' ? system : theme;
  React.useEffect(() => {
    apply(resolvedTheme);
  }, [resolvedTheme]);

  const setTheme = React.useCallback((next: Theme) => {
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // tiltott tároló: a választás csak erre a munkamenetre él — a felület ettől még vált
    }
    setThemeState(next);
  }, []);

  const value = React.useMemo(
    () => ({ theme, resolvedTheme, setTheme }),
    [theme, resolvedTheme, setTheme],
  );
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = React.useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider');
  return ctx;
}
